/**
 * ActivityMap — full-canvas map for the OEM Home Dashboard.
 *
 * Two render modes (POC):
 *  - RESTING (plotMode = null): plots REAL individual asset points (clustered),
 *    name-labelled, coloured by fleet category (Ready / Needs Repair / Out of
 *    Service). Consumes `useHomeEntityPoints(range, ASSET)`.
 *  - PLOTTING (plotMode = ASSET | ACTIVITY | ROUTINE | PRODUCT): a card is
 *    selected, so the map plots that entity's individual points, rendered
 *    CLUSTERED (bounded but large for assets) and coloured by status. Consumes
 *    `useHomeEntityPoints(range, plotMode)`. ROUTINE plots assets that have
 *    routine activity, coloured by routine; PRODUCT plots located assets,
 *    coloured by product.
 *
 * `searchQuery` filters the visible markers by name (client-side, case-insensitive).
 *
 * Web-only — do NOT import from mobile code.
 */

import { HomeEntityType } from '@gripp/shared-logic';
import { tokens } from '@gripp/shared-ui';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { StyleSheet, Text, View } from 'react-native';
import {
  useHomeEntityPoints,
  type HomeMapPoint,
} from '../hooks/useHomeEntityPoints';
import { type TimeRange } from '../types';
import { type MapMarker, MapView } from './mapView';

interface ActivityMapProps {
  range: TimeRange;
  /** Active plotting mode (null = resting view, which plots ASSET points). */
  plotMode?: HomeEntityType | null;
  /**
   * Sub-filter on the point `status` (asset category, activity bucket, routine
   * name, or product name) set by clicking a chart item. null = show all points.
   */
  statusFilter?: string | null;
  /** Case-insensitive name filter applied to the visible markers. */
  searchQuery?: string;
}

// Severity-derived activity status → Crop Circle semantic colour (matches the
// Activity card's Solved/Pending/Urgent palette).
const ACTIVITY_STATUS_COLORS: Record<string, string> = {
  urgent: tokens.colors.semantic.error,
  pending: tokens.colors.semantic.warning,
  solved: tokens.colors.semantic.success,
};

// Asset fleet category → Crop Circle semantic colour (matches the Assets card
// donut). Keyed by the real status labels the backend now returns.
const ASSET_CATEGORY_COLORS: Record<string, string> = {
  Ready: tokens.colors.semantic.success, // green
  'Needs Repair': tokens.colors.semantic.warning, // yellow
  'Out of Service': tokens.colors.semantic.error, // red
};
// Fallback for any unexpected / missing status label.
const ASSET_CATEGORY_FALLBACK = tokens.colors.slate[400];

// Categorical label → colour. Routines and products aren't good/bad, so use the
// Crop Circle label palette (distinct hues) assigned deterministically by name,
// so a given routine / product keeps the same colour across renders. Shared by
// the ROUTINE (by routine name) and PRODUCT (by product name) plotting modes.
const CATEGORICAL_PALETTE: string[] = [
  tokens.colors.label.indigo,
  tokens.colors.label.teal,
  tokens.colors.label.magenta,
  tokens.colors.label.violet,
  tokens.colors.label.sand,
  tokens.colors.label.cyan,
];
function categoricalColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return CATEGORICAL_PALETTE[Math.abs(hash) % CATEGORICAL_PALETTE.length];
}

/** Converts a plotting-mode `HomeMapPoint` to a coloured `MapMarker`. */
function entityPointToMarker(
  point: HomeMapPoint,
  mode: HomeEntityType
): MapMarker {
  const status = point.status ?? '';
  let color: string = tokens.colors.semantic.success;
  let bucket: MapMarker['bucket'];

  if (mode === HomeEntityType.Activity) {
    color = ACTIVITY_STATUS_COLORS[status] ?? tokens.colors.semantic.success;
    if (status === 'urgent' || status === 'pending' || status === 'solved') {
      bucket = status;
    }
  } else if (mode === HomeEntityType.Asset) {
    color = ASSET_CATEGORY_COLORS[status] ?? ASSET_CATEGORY_FALLBACK;
  } else if (mode === HomeEntityType.Routine) {
    // status is the routine name; colour categorically by routine.
    color = categoricalColor(status);
  } else if (mode === HomeEntityType.Product) {
    // status is the product name; colour categorically by product.
    color = categoricalColor(status);
  }

  return {
    id: point.id,
    name: point.name,
    lat: point.lat,
    lng: point.lng,
    color,
    bucket,
  };
}

const DEFAULT_CENTER: [number, number] = [0, 20]; // [lng, lat]
const DEFAULT_ZOOM = 1.5;

export function ActivityMap({
  range,
  plotMode,
  statusFilter,
  searchQuery,
}: ActivityMapProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // RESTING (plotMode null) defaults to real ASSET points so the home map shows
  // the live fleet, coloured by category. PLOTTING uses the selected entity.
  // Either way the map plots individual entity points, clustered.
  const effectiveMode = plotMode ?? HomeEntityType.Asset;
  const entityQuery = useHomeEntityPoints(range, effectiveMode);

  // Clicking an individual point opens that entity's detail page. ACTIVITY
  // markers carry an issue id (→ activity detail); ASSET, ROUTINE and PRODUCT
  // markers all carry an asset id (→ asset detail).
  const handleMarkerClick = useCallback(
    (marker: MapMarker) => {
      if (effectiveMode === HomeEntityType.Activity) {
        navigate(`/activities/${marker.id}`);
      } else {
        // ASSET, ROUTINE and PRODUCT markers all carry an asset id → open the asset.
        navigate(`/assets/${marker.id}`);
      }
    },
    [effectiveMode, navigate]
  );

  const isLoading = entityQuery.isLoading;
  const error = entityQuery.error;

  const markers = useMemo(() => {
    let points = entityQuery.data ?? [];
    // Sub-filter from a clicked chart item (matches the point's status:
    // asset category / activity bucket / routine name / product name).
    if (statusFilter) {
      points = points.filter((p) => p.status === statusFilter);
    }
    let base = points.map((p) => entityPointToMarker(p, effectiveMode));

    const q = searchQuery?.trim().toLowerCase();
    if (q) base = base.filter((m) => m.name.toLowerCase().includes(q));
    return base;
  }, [effectiveMode, entityQuery.data, statusFilter, searchQuery]);

  // The basemap always renders; loading / error / empty are overlays on top.
  let status: { message: string; testID: string } | null = null;
  if (isLoading) {
    status = {
      message: t('home.map.loading', 'Loading map…'),
      testID: 'activity-map-loading',
    };
  } else if (error) {
    status = {
      message: t('home.map.error', 'Unable to load map data.'),
      testID: 'activity-map-error',
    };
  } else if (markers.length === 0) {
    // Empty copy follows the plotted entity (resting defaults to ASSET) so it
    // never reads "No assets" while plotting activities/routines/products.
    const emptyByMode: Record<HomeEntityType, string> = {
      [HomeEntityType.Asset]: t('home.map.emptyAssets', 'No assets for this period.'),
      [HomeEntityType.Activity]: t('home.map.emptyActivity', 'No activity for this period.'),
      [HomeEntityType.Routine]: t('home.map.emptyRoutines', 'No routine activity for this period.'),
      [HomeEntityType.Product]: t('home.map.emptyProducts', 'No products for this period.'),
    };
    status = {
      message: searchQuery?.trim()
        ? t('home.map.noResults', 'No matches for this search.')
        : emptyByMode[effectiveMode] ?? t('home.map.empty', 'No data for this period.'),
      testID: 'activity-map-empty',
    };
  }

  return (
    <View style={styles.fill} testID="activity-map">
      <MapView
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        markers={markers}
        onMarkerClick={handleMarkerClick}
        fitToMarkers
        clustered
      />
      {status && (
        <View
          style={styles.statusOverlay}
          pointerEvents="none"
          testID={status.testID}
        >
          <Text style={styles.statusText}>{status.message}</Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  fill: {
    flex: 1,
  },
  statusOverlay: {
    position: 'absolute',
    bottom: tokens.spacing.lg * 5, // above the card strip
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  statusText: {
    ...tokens.typography.label.medium,
    color: tokens.colors.neutral[600],
    backgroundColor: `${tokens.colors.brand.white}D9`, // ~85% opacity
    paddingVertical: tokens.spacing.xs,
    paddingHorizontal: tokens.spacing.md,
    borderRadius: tokens.borderRadius.full,
    overflow: 'hidden',
  },
});
